import React, { useState, useEffect } from 'react';
import {
  Compass,
  Calendar,
  Camera,
  ArrowRight,
  Download,
  Lock,
} from 'lucide-react';
import { storageService } from '@/services/storage';

export interface CatalogTripItem {
  id: string | number;
  name: string;
  startDate?: number;
  endDate?: number;
  locationSummary?: string;
  photoCount: number;
  stepCount?: number;
  categories: string[];
  coverKey?: string;
  coverDataUrl?: string;
  isDemo?: boolean;
  isComingSoon?: boolean;
}

export interface TravelCatalogViewProps {
  trips: CatalogTripItem[];
  onSelectTrip: (tripId: string | number, isDemo?: boolean) => void;
  onOpenAdmin: () => void;
  canInstall?: boolean;
  onInstall?: () => void;
}

// Iceland Nordic Aurora Artistic SVG Cover
export const ICELAND_COVER_SVG = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 675" width="1200" height="675">
  <defs>
    <linearGradient id="skyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#020617"/>
      <stop offset="45%" stop-color="#091427"/>
      <stop offset="85%" stop-color="#0f2636"/>
      <stop offset="100%" stop-color="#022c22"/>
    </linearGradient>
    <linearGradient id="auroraGrad1" x1="0%" y1="100%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="#10b981" stop-opacity="0"/>
      <stop offset="35%" stop-color="#34d399" stop-opacity="0.8"/>
      <stop offset="70%" stop-color="#6ee7b7" stop-opacity="0.4"/>
      <stop offset="100%" stop-color="#38bdf8" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="auroraGrad2" x1="100%" y1="100%" x2="0%" y2="0%">
      <stop offset="0%" stop-color="#065f46" stop-opacity="0"/>
      <stop offset="40%" stop-color="#10b981" stop-opacity="0.6"/>
      <stop offset="80%" stop-color="#a7f3d0" stop-opacity="0.3"/>
      <stop offset="100%" stop-color="#818cf8" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="mountGradBack" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#1e293b"/>
      <stop offset="100%" stop-color="#020617"/>
    </linearGradient>
    <linearGradient id="mountGradFront" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#0f172a"/>
      <stop offset="100%" stop-color="#020617"/>
    </linearGradient>
    <linearGradient id="glacierGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#e0f2fe"/>
      <stop offset="60%" stop-color="#38bdf8"/>
      <stop offset="100%" stop-color="#0369a1"/>
    </linearGradient>
  </defs>

  <rect width="1200" height="675" fill="url(#skyGrad)"/>
  <path d="M 0,260 Q 300,120 600,240 T 1200,180 L 1200,380 Q 900,260 600,360 T 0,380 Z" fill="url(#auroraGrad1)" opacity="0.85"/>
  <path d="M 0,220 Q 350,90 750,220 T 1200,160 L 1200,320 Q 800,220 400,300 T 0,320 Z" fill="url(#auroraGrad2)" opacity="0.7"/>

  <polygon points="120,500 280,310 440,500" fill="url(#mountGradBack)"/>
  <polygon points="260,310 280,310 320,380 240,380" fill="url(#glacierGrad)" opacity="0.9"/>

  <polygon points="360,500 580,240 800,500" fill="url(#mountGradBack)"/>
  <polygon points="560,240 580,240 640,340 520,340" fill="url(#glacierGrad)" opacity="0.95"/>

  <polygon points="720,500 920,280 1120,500" fill="url(#mountGradBack)"/>
  <polygon points="900,280 920,280 970,350 870,350" fill="url(#glacierGrad)" opacity="0.9"/>

  <polygon points="0,530 200,420 420,530" fill="url(#mountGradFront)"/>
  <polygon points="320,530 520,390 760,530" fill="url(#mountGradFront)"/>
  <polygon points="660,530 890,380 1140,530" fill="url(#mountGradFront)"/>
  <polygon points="960,530 1100,430 1200,530" fill="url(#mountGradFront)"/>

  <rect y="500" width="1200" height="175" fill="#030712"/>
  <line x1="100" y1="520" x2="1100" y2="520" stroke="#10b981" stroke-width="1.5" opacity="0.3"/>
  <line x1="250" y1="550" x2="950" y2="550" stroke="#34d399" stroke-width="1.2" opacity="0.2"/>
  <line x1="400" y1="590" x2="800" y2="590" stroke="#38bdf8" stroke-width="1" opacity="0.15"/>
</svg>
`)}`;

// Japan Mount Fuji Twilight Artistic SVG Cover
export const JAPAN_COMING_SOON_COVER_SVG = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 675" width="1200" height="675">
  <defs>
    <linearGradient id="jpSkyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#0b0f19"/>
      <stop offset="30%" stop-color="#1e1b4b"/>
      <stop offset="65%" stop-color="#4c0519"/>
      <stop offset="85%" stop-color="#881337"/>
      <stop offset="100%" stop-color="#f43f5e"/>
    </linearGradient>
    <radialGradient id="sunGrad" cx="50%" cy="55%" r="35%">
      <stop offset="0%" stop-color="#fda4af" stop-opacity="0.95"/>
      <stop offset="40%" stop-color="#f43f5e" stop-opacity="0.8"/>
      <stop offset="80%" stop-color="#be123c" stop-opacity="0.4"/>
      <stop offset="100%" stop-color="#881337" stop-opacity="0"/>
    </radialGradient>
    <linearGradient id="fujiGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#1e1b4b"/>
      <stop offset="100%" stop-color="#090d16"/>
    </linearGradient>
  </defs>

  <rect width="1200" height="675" fill="url(#jpSkyGrad)"/>
  <circle cx="600" cy="380" r="220" fill="url(#sunGrad)"/>

  <polygon points="200,520 540,240 660,240 1000,520" fill="url(#fujiGrad)"/>
  <polygon points="540,240 660,240 710,295 670,305 640,285 615,310 585,290 560,310 515,285 490,295" fill="#f8fafc" opacity="0.95"/>

  <polygon points="0,540 280,450 560,540" fill="#0f172a" opacity="0.85"/>
  <polygon points="640,540 920,440 1200,540" fill="#0f172a" opacity="0.85"/>

  <!-- Torii Gate Silhouette -->
  <rect x="535" y="440" width="12" height="90" fill="#020617"/>
  <rect x="653" y="440" width="12" height="90" fill="#020617"/>
  <path d="M 500,445 Q 600,435 700,445 L 700,457 Q 600,448 500,457 Z" fill="#020617"/>
  <rect x="525" y="465" width="150" height="8" fill="#020617"/>

  <rect y="520" width="1200" height="155" fill="#070a13"/>
  <line x1="300" y1="540" x2="900" y2="540" stroke="#f43f5e" stroke-width="1.5" opacity="0.4"/>
  <line x1="420" y1="565" x2="780" y2="565" stroke="#fda4af" stroke-width="1.2" opacity="0.3"/>
  <line x1="500" y1="590" x2="700" y2="590" stroke="#fb7185" stroke-width="1" opacity="0.2"/>
</svg>
`)}`;

// Backward-compatibility alias
export const MADEIRA_DEMO_COVER_SVG = ICELAND_COVER_SVG;

function formatDateRange(start?: number, end?: number): string {
  if (!start) return 'Date non disponibili';
  try {
    const s = new Date(start * 1000);
    const startStr = s.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
    if (!end || end === start) {
      return `${startStr} ${s.getFullYear()}`;
    }
    const e = new Date(end * 1000);
    const endStr = e.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startStr} – ${endStr}`;
  } catch {
    return 'Periodo di viaggio';
  }
}

export const TravelCatalogView: React.FC<TravelCatalogViewProps> = ({
  trips,
  onSelectTrip,
  onOpenAdmin,
  canInstall: _canInstall,
  onInstall,
}) => {
  return (
    <div className="flex-1 overflow-y-auto bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100 flex flex-col font-sans">
      {/* 1. Hero Showcase Section: Birthday Dedication */}
      <section className="relative px-4 sm:px-6 lg:px-12 pt-8 sm:pt-12 pb-6 max-w-7xl mx-auto w-full">
        {/* Subtle decorative glow in background */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-sky-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="relative flex flex-col items-center text-center space-y-3.5 max-w-3xl mx-auto">
          {/* Dedication Badge */}
          <div className="inline-flex items-center px-3.5 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-medium backdrop-blur-md shadow-sm">
            <span>I nostri ricordi di viaggio</span>
          </div>

          {/* Birthday Heading */}
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
            Buon Compleanno <span className="text-sky-400">Chiaretti</span>
          </h1>

          {/* Personal Subtitle */}
          <p className="text-sm sm:text-base text-slate-300 max-w-2xl leading-relaxed">
            ho creato questa app solo per te, per dare una casa a nostri ricordi! Pian piano la sistemiamo, modifichiamo ed arrediamo per come ci piace
          </p>

          {/* Hero Action: PWA Install Button only */}
          {onInstall && (
            <div className="pt-2">
              <button
                type="button"
                onClick={onInstall}
                aria-label="Installa applicazione su schermata Home"
                className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-500 hover:to-blue-500 text-white rounded-xl text-sm font-semibold shadow-lg shadow-sky-500/25 border border-sky-400/30 transition-all active:scale-95 cursor-pointer"
              >
                <Download className="w-4 h-4" />
                <span>Installa App Offline</span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* 2. Travel Cards Grid */}
      <section className="px-4 sm:px-6 lg:px-12 py-6 max-w-7xl mx-auto w-full flex-1">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <Compass className="w-6 h-6 text-sky-400" />
            <h2 className="text-xl sm:text-2xl font-bold text-white tracking-wide">
              I Nostri Viaggi ({trips.length})
            </h2>
          </div>

          <span className="text-xs sm:text-sm text-slate-400 font-medium">
            Seleziona un viaggio per esplorare la mappa
          </span>
        </div>

        <div
          className={`grid gap-8 sm:gap-10 ${
            trips.length === 1
              ? 'max-w-2xl mx-auto grid-cols-1'
              : 'max-w-5xl mx-auto grid-cols-1 md:grid-cols-2'
          }`}
        >
          {trips.map((trip) => (
            <TripCatalogCard
              key={trip.id}
              trip={trip}
              onSelect={() => onSelectTrip(trip.id, trip.isDemo)}
            />
          ))}
        </div>
      </section>

      {/* 3. Minimal Clean Footer */}
      <footer className="px-4 sm:px-6 lg:px-12 py-8 max-w-7xl mx-auto w-full mt-auto">
        <div className="pt-6 border-t border-slate-900 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-3">
          <span>Travel Photo Map &bull; I nostri ricordi di viaggio</span>
          <button
            type="button"
            onClick={onOpenAdmin}
            className="text-slate-500 hover:text-slate-400 flex items-center gap-1.5 transition cursor-pointer"
            title="Area Amministratore per caricamento itinerari"
          >
            <Lock className="w-3.5 h-3.5" />
            <span>Pannello Riservato Amministratore</span>
          </button>
        </div>
      </footer>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Trip Card Component with Cover Photo Resolution & Coming Soon Support
// ----------------------------------------------------------------------------

interface TripCatalogCardProps {
  trip: CatalogTripItem;
  onSelect: () => void;
}

const TripCatalogCard: React.FC<TripCatalogCardProps> = ({ trip, onSelect }) => {
  const defaultFallbackCover = trip.isComingSoon
    ? JAPAN_COMING_SOON_COVER_SVG
    : ICELAND_COVER_SVG;

  const [coverUrl, setCoverUrl] = useState<string | null>(trip.coverDataUrl || null);

  useEffect(() => {
    let isCancelled = false;

    if (trip.coverDataUrl) {
      setCoverUrl(trip.coverDataUrl);
      return;
    }

    if (trip.isComingSoon) {
      setCoverUrl(JAPAN_COMING_SOON_COVER_SVG);
      return;
    }

    if (trip.isDemo) {
      setCoverUrl(ICELAND_COVER_SVG);
      return;
    }

    if (trip.coverKey) {
      storageService
        .getPhotoUrl(trip.coverKey)
        .then((url) => {
          if (!isCancelled && url) {
            setCoverUrl(url);
          }
        })
        .catch(() => {
          if (!isCancelled) setCoverUrl(defaultFallbackCover);
        });
    } else {
      setCoverUrl(defaultFallbackCover);
    }

    return () => {
      isCancelled = true;
    };
  }, [trip.coverKey, trip.coverDataUrl, trip.isDemo, trip.isComingSoon, defaultFallbackCover]);

  const dateLabel = formatDateRange(trip.startDate, trip.endDate);

  return (
    <div
      onClick={trip.isComingSoon ? undefined : onSelect}
      className={`group relative bg-slate-900/90 border rounded-3xl overflow-hidden shadow-2xl transition-all duration-300 flex flex-col ${
        trip.isComingSoon
          ? 'border-slate-800/80 cursor-default'
          : 'hover:bg-slate-900 border-slate-800/80 hover:border-sky-500/50 hover:shadow-sky-500/10 cursor-pointer hover:-translate-y-1.5'
      }`}
    >
      {/* Cover Image Container */}
      <div className="relative aspect-[16/10] w-full bg-slate-950 overflow-hidden">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={trip.name}
            className={`w-full h-full object-cover transition-transform duration-700 ease-out ${
              trip.isComingSoon ? '' : 'group-hover:scale-105'
            }`}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-slate-900 flex items-center justify-center text-slate-600">
            <Compass className="w-14 h-14" />
          </div>
        )}

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-transparent to-transparent opacity-70" />

        {/* Top Badges: Fixed Solid Coming Soon Tag (if applicable) */}
        {trip.isComingSoon && (
          <div className="absolute top-4 right-4 z-10 pointer-events-none">
            <span className="px-3.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-slate-800 text-sky-400 border border-slate-700 shadow-sm">
              Coming Soon
            </span>
          </div>
        )}

        {/* Bottom Title on Image */}
        <div className="absolute bottom-4 left-4 right-4 z-10 pointer-events-none">
          <h3 className="text-xl sm:text-2xl font-extrabold text-white drop-shadow-md group-hover:text-sky-300 transition-colors truncate">
            {trip.name}
          </h3>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-300 font-medium mt-1.5">
            <Calendar className="w-4 h-4 text-sky-400/90 shrink-0" />
            <span>{trip.isComingSoon ? 'In arrivo per i prossimi viaggi' : dateLabel}</span>
          </div>
        </div>
      </div>

      {/* Card Body & Details: Photo Count Only (Sober & Minimal) */}
      <div className="p-5 sm:p-6 flex flex-col flex-1 justify-between gap-5">
        <div className="flex flex-col gap-3.5">
          {!trip.isComingSoon && (
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-slate-800/80 border border-slate-700/60 text-xs sm:text-sm font-medium text-slate-200 shadow-sm">
                <Camera className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-sky-400 shrink-0" />
                <span>{trip.photoCount} foto</span>
              </div>
            </div>
          )}
        </div>

        {/* Primary CTA Button */}
        {trip.isComingSoon ? (
          <div className="w-full mt-2 py-3.5 sm:py-4 px-6 rounded-2xl bg-slate-800/50 border border-slate-700/50 text-slate-400 text-sm sm:text-base font-semibold flex items-center justify-center cursor-not-allowed select-none">
            <span>Coming Soon</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            className="w-full mt-2 py-3.5 sm:py-4 px-6 rounded-2xl bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-600 hover:from-sky-500 hover:via-blue-500 hover:to-indigo-500 text-white text-sm sm:text-base font-bold shadow-lg shadow-sky-500/20 group-hover:shadow-sky-500/35 transition-all flex items-center justify-center gap-2.5 cursor-pointer active:scale-[0.98]"
          >
            <span>Esplora sulla Mappa</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        )}
      </div>
    </div>
  );
};
